"use client";

import Link from "next/link";
import { notFound } from "next/navigation";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import {
  GuidesPageLayout,
  FaqList,
  SectionHeading,
  type GuideSection as GuideSidebarSection,
} from "@/components/GuidesShared";
import { SectionForum } from "@/components/SectionForum";
import { getGuideManifest } from "@/lib/guides/manifests";
import type { GuideId } from "@/lib/guides/types";

export default function GuideSectionView({
  guideId,
  sectionId,
}: {
  guideId: GuideId;
  sectionId: string;
}) {
  const manifest = getGuideManifest(guideId);
  if (!manifest) notFound();

  const section = manifest.sections.find((s) => s.id === sectionId);
  if (!section) notFound();

  const sidebarSections: GuideSidebarSection[] = [
    {
      id: manifest.id,
      label: manifest.breadcrumb,
      icon: section.icon,
      items: manifest.sections.map((s) => ({
        id: s.id,
        label: s.label,
      })),
    },
  ];

  const idx = manifest.sections.findIndex((s) => s.id === sectionId);
  const prev = idx > 0 ? manifest.sections[idx - 1] : null;
  const next = idx >= 0 && idx < manifest.sections.length - 1 ? manifest.sections[idx + 1] : null;

  return (
    <GuidesPageLayout
      sections={sidebarSections}
      title={section.label}
      subtitle={section.shortDescription}
      breadcrumb={`${manifest.breadcrumb} · ${section.label}`}
      backHref={`/guides/${manifest.id}`}
      linkPrefix={`/guides/${manifest.id}/`}
      activeId={section.id}
    >
      <div className="space-y-8 pb-20">
        <section className="space-y-6">{section.content()}</section>

        {section.toolHref ? (
          <Link
            href={section.toolHref}
            className="group flex items-center justify-between gap-3 rounded-xl border border-primary/25 bg-primary/8 px-4 py-3.5 text-sm transition-colors hover:border-primary/40 hover:bg-primary/12"
          >
            <span className="font-medium text-foreground">
              Open {section.toolLabel ?? section.label} and start building
            </span>
            <ArrowUpRight className="h-4 w-4 text-primary transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        ) : null}

        {section.faqs?.length ? (
          <section className="space-y-4">
            <SectionHeading id="faq">Frequently asked questions</SectionHeading>
            <FaqList items={section.faqs} />
          </section>
        ) : null}

        <SectionForum guideId={manifest.id} sectionId={section.id} sectionLabel={section.label} />

        <nav className="flex flex-col gap-3 border-t border-border/50 pt-6 sm:flex-row sm:items-center sm:justify-between">
          {prev ? (
            <Link
              href={`/guides/${manifest.id}/${prev.id}`}
              className="group flex items-center gap-2 rounded-xl border border-border/60 bg-card px-4 py-3 text-sm transition-colors hover:border-primary/30 hover:bg-muted/40"
            >
              <ChevronLeft className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
              <span className="text-muted-foreground">Previous</span>
              <span className="font-medium text-foreground">{prev.label}</span>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link
              href={`/guides/${manifest.id}/${next.id}`}
              className="group flex items-center gap-2 rounded-xl border border-border/60 bg-card px-4 py-3 text-sm transition-colors hover:border-primary/30 hover:bg-muted/40 sm:ml-auto"
            >
              <span className="text-muted-foreground">Next</span>
              <span className="font-medium text-foreground">{next.label}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
            </Link>
          ) : null}
        </nav>
      </div>
    </GuidesPageLayout>
  );
}
